<template>
  <div class="api-container">
    <v-row no-gutters class="flex-grow-0">
      <v-col cols="12" md="6">
        <v-card>
          <span>
            <v-card-title class="header-title" data-cy="api-secret-subtitle">
              {{ $t('apiServices.titleDeveloperPortal') }}
            </v-card-title>
            <v-card-text>
              <v-btn
                class="my-2"
                color="primary"
                append-icon="mdi-open-in-new"
                @click="openPortalLink()"
                data-cy="open-portal-button"
              >
                {{ $t('apiServices.buttonOpenPortal') }}
              </v-btn>
              <v-spacer />
              <p class="my-0" data-cy="api-portal-description">
                {{ $t('apiServices.portalDescription') }}
              </p>
            </v-card-text>
          </span>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card>
          <v-card-title class="header-title">
            {{ $t('accountSubscriptions.dataServicesSubscriptions') }}
          </v-card-title>
          <v-card-text class="px-0 pb-2">
            <div class="text-center" v-if="removeLock || subLock">
              <v-progress-circular indeterminate color="primary" class="ma-4" />
            </div>
            <v-list v-else density="compact" class="inline">
              <v-list-item v-for="(sub, i) in dataSubscriptions" :key="i">
                <v-list-item-title>{{ sub.name }}</v-list-item-title>
                <v-list-item-subtitle>
                  {{ $t('accountSubscriptions.deviceAllowance') }}
                  {{
                    $t('accountSubscriptions.deviceSubscriptionsUsedRemaining', {
                      qty: sub.totalUsed,
                      remaining: sub.totalQtyRemaining
                    })
                  }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <div class="registered-devices-container">
      <v-card class="registered-devices-card" :class="{ 'fit-to-space': !isMobile }">
        <v-card-title class="d-flex flex-wrap justify-space-between">
          <div>{{ $t('apiServices.registeredDevices') }}</div>

          <v-btn
            variant="text"
            class="text-black register-btn"
            @click="dialogs.registerDevicesDialog.open = true"
            data-cy="registerDevicesDialogButton"
            :disabled="removeLock || subLock"
          >
            <v-icon class="header-icon" icon="mdi-map-marker-plus" />
            {{ $t('apiServices.registerDevices') }}
          </v-btn>
        </v-card-title>
        <v-data-table
          v-if="!removeLock && !subLock"
          :headers="registeredDevices.headers"
          :hide-default-header="smAndDown"
          :items="devices"
          :mobile-breakpoint="thresholds.md"
          :items-per-page-text="isMobile ? 'tables.rows' : 'tables.rowsPerPage'"
          :items-per-page-options="[10, 25, 50]"
          :class="registeredDevicesClass"
        >
          <!-- item slot -->
          <template v-slot:item="{ item }">
            <!-- desktop view -->
            <tr v-if="!isMobile" class="registered-device-table" :data-updated="updateForced">
              <td class="text-black">{{ item.serial }}</td>
              <td style="max-width: 300px">
                <v-tooltip
                  location="bottom"
                  :max-width="600"
                  :disabled="!isNameOverflowing[item.id]"
                >
                  <template v-slot:activator="{ props }">
                    <div v-bind="props" class="device-name" :ref="item.id">
                      <span>{{ item.deviceName }}</span>
                    </div>
                  </template>
                  <span>{{ item.deviceName }}</span>
                </v-tooltip>
              </td>
              <td>
                <span>{{ item.dataServices }}</span>
              </td>
              <td>
                <template v-if="deviceHasWebsiteSubscription(item)">
                  <span v-if="item.websiteSubscription">{{ item.websiteSubscription }}</span>
                </template>
                <span v-else>
                  {{ $t('apiServices.table.notRegistered') }}
                </span>
              </td>
              <td class="text-end">
                <ActionMenu
                  :deviceId="item.id"
                  :websiteSubscription="item.websiteSubscription"
                  :removeLock="removeLock"
                />
              </td>
            </tr>
            <!-- mobile view -->
            <tr v-else class="registered-device-mobile-table" :data-updated="updateForced">
              <td class="mobile-list">
                <div class="item-title">
                  <ActionMenu
                    :deviceId="item.id"
                    :websiteSubscription="item.websiteSubscription"
                    :removeLock="removeLock"
                  />
                  <div class="title-text">
                    <span>
                      {{ item.serial }}
                    </span>
                  </div>
                </div>
                <v-row no-gutters>
                  <v-col>
                    <ul>
                      <li v-if="item.deviceName" class="list-item">
                        <span class="mobile-header">
                          {{ $t('apiServices.table.deviceName') }}:
                        </span>
                        <v-tooltip
                          location="bottom"
                          :max-width="600"
                          :disabled="!isNameOverflowing[item.id]"
                        >
                          <template v-slot:activator="{ props }">
                            <span v-bind="props" class="mobile-field device-name" :ref="item.id">
                              <span>{{ item.deviceName }}</span>
                            </span>
                          </template>
                          <span>{{ item.deviceName }}</span>
                        </v-tooltip>
                      </li>
                      <li class="list-item">
                        <span class="mobile-header">
                          {{ $t('apiServices.table.dataServices') }}:
                        </span>
                        <span class="mobile-field">
                          {{ item.dataServices }}
                        </span>
                      </li>
                      <li class="list-item">
                        <span class="mobile-header">
                          {{ $t('apiServices.table.websiteSubs') }}:
                        </span>
                        <template v-if="deviceHasWebsiteSubscription(item)">
                          <span v-if="item.websiteSubscription" class="mobile-field">
                            {{ item.websiteSubscription }}
                          </span>
                        </template>
                        <span v-else class="mobile-field">
                          {{ $t('apiServices.table.notRegistered') }}
                        </span>
                      </li>
                    </ul>
                  </v-col>
                </v-row>
              </td>
            </tr>
          </template>
        </v-data-table>
        <!-- progress spinner -->
        <div v-else class="text-center emptyMessage">
          <v-progress-circular indeterminate color="primary"></v-progress-circular>
        </div>
      </v-card>
    </div>
    <RegisterDevicesDialog
      :open="dialogs.registerDevicesDialog.open"
      :removeLock="removeLock"
      :subLock="subLock"
      @close="registerDevicesDialogClose"
      @getUserDevices="getUserDevices"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, nextTick } from 'vue'
import { isChildOverflowing } from '../services/html-utilities'
import debounce from 'lodash/debounce'
import { useDisplay } from 'vuetify'
import type { DataTableHeader } from 'vuetify'
import RegisterDevicesDialog from '../components/api/RegisterDevicesDialog.vue'
import ActionMenu from '../components/api/ActionMenu.vue'
import type { EnrichedSubscription } from '@/store/subscriptions/types'

const PORTAL_LINK = 'https://developers.tsilink.com'

interface ApiDeviceRow {
  id: string
  serial: string
  deviceName: string
  dataServices: string | undefined
  websiteSubscription: string | undefined
}

export default defineComponent({
  name: 'Api',
  setup() {
    const { smAndDown, thresholds } = useDisplay()
    return { smAndDown, thresholds }
  },
  components: {
    RegisterDevicesDialog,
    ActionMenu
  },
  data() {
    return {
      keys: [] as string[],
      removeLock: false,
      subLock: false,
      addToBothWebsiteAndDataServices: false,
      dialogs: {
        registerDevicesDialog: {
          open: false
        }
      },
      registeredDevices: {
        headers: [
          {
            title: this.$t('apiServices.table.serialNumber'),
            align: 'start',
            key: 'serialNumber',
            sortable: false
          },
          {
            title: this.$t('apiServices.table.deviceName'),
            align: 'start',
            key: 'deviceName',
            sortable: false
          },
          {
            title: this.$t('apiServices.table.dataServices'),
            key: 'dataServicesSubs',
            sortable: false
          },
          {
            title: this.$t('apiServices.table.websiteSubs'),
            key: 'websiteSubscription',
            sortable: false
          },
          {
            title: this.$t('apiServices.table.actions'),
            key: 'actions',
            align: 'end',
            sortable: false
          }
        ] as DataTableHeader<ApiDeviceRow>[]
      },
      isNameOverflowing: {} as Record<string, boolean>,
      updateForced: false,
      observer: null as ResizeObserver | null
    }
  },
  mounted() {
    this.observe(this.$el)
    this.getUserDevices()
  },
  async updated() {
    if (!this.removeLock && !this.subLock) {
      // The DOM needs to be patched before a name overflow can be detected
      // Then a second update is forced to patch the DOM again
      // and enable tooltips where necessary
      await this.setIsNameOverflowing()
      if (!this.updateForced) {
        this.$forceUpdate()
        this.updateForced = true
      } else {
        this.updateForced = false
      }
    }
  },
  unmounted() {
    if (this.observer) this.observer.disconnect()
  },
  computed: {
    isMobile(): boolean {
      return this.smAndDown
    },
    dataSubscriptions(): EnrichedSubscription[] {
      return this.$store.getters['subscriptions/getDataServices']
    },
    devices(): ApiDeviceRow[] {
      const keys = Object.keys(this.$store.state['devices'].serial)
      const devs: ApiDeviceRow[] = []
      for (const key of keys) {
        const isShared = this.$store.getters['devices/getIsSharedByDeviceId'](key)
        const serial = this.$store.getters['devices/getSerialByDeviceId'](key)
        const deviceName = this.$store.getters['devices/getNameByDeviceId'](key)
        const isDataServices = this.deviceInDataServices(serial)
        if (serial && isDataServices && !isShared) {
          const dev: ApiDeviceRow = {
            id: key,
            serial: serial,
            deviceName: deviceName,
            dataServices: this.getDataServiceSubName(serial),
            websiteSubscription: this.getWebsiteSubName(serial)
          }
          devs.push(dev)
        }
      }
      return devs
    },
    registeredDevicesClass() {
      return { mobile: this.isMobile }
    }
  },
  methods: {
    getDataServiceSubName(serial: string): string | undefined {
      if (this.$store.getters['subscriptions/getDeviceDataServices']) {
        const dataServiceTier =
          this.$store.getters['subscriptions/getDeviceDataServicesByDeviceSerial'](serial)
        if (dataServiceTier) {
          return this.$store.getters['subscriptions/getSKUMetadataNameById'](dataServiceTier)
        }
      }
      return undefined
    },
    getWebsiteSubName(serial: string): string | undefined {
      const premiumTier =
        this.$store.getters['subscriptions/getDevicePremiumSubscriptionByDeviceSerial'](serial)
      if (premiumTier) {
        return this.$store.getters['subscriptions/getSKUMetadataNameById'](premiumTier)
      }
      const basicTier =
        this.$store.getters['subscriptions/getDeviceBasicSubscriptionByDeviceSerial'](serial)
      if (basicTier) {
        return this.$store.getters['subscriptions/getSKUMetadataNameById'](basicTier)
      }
      return undefined
    },
    deviceHasWebsiteSubscription(device: ApiDeviceRow): boolean {
      return !!device.websiteSubscription
    },
    deviceInDataServices(serial: string): boolean {
      return this.$store.getters['subscriptions/hasDataServicesSubscription'](serial)
    },
    deviceInWebsite(serial: string): boolean {
      return this.$store.getters['subscriptions/hasWebsiteSubscription'](serial)
    },
    registerDevicesDialogClose() {
      this.dialogs.registerDevicesDialog.open = false
    },
    async getUserDevices() {
      this.removeLock = true
      await this.$store.dispatch('devices/setAllDevices')
      const devices = this.$store.state.devices.allDevices
      this.subLock = true
      await this.$store.dispatch('subscriptions/updateSubscriptions', {
        auth: this.$auth,
        api: this.$api,
        devices
      })
      this.subLock = false
      this.removeLock = false
    },
    openPortalLink() {
      window.open(PORTAL_LINK, '_blank', 'noopener,noreferrer')
    },
    async setIsNameOverflowing() {
      await nextTick()
      this.devices.forEach((item: ApiDeviceRow) => {
        this.isNameOverflowing[item.id] = isChildOverflowing(this.$refs[item.id] as HTMLElement)
      })
    },
    observe(el: Element | null) {
      const forceUpdate = debounce(() => {
        this.$forceUpdate()
      }, 200)

      this.observer = new ResizeObserver(forceUpdate)
      if (el instanceof Element) {
        this.observer.observe(el)
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.api-container {
  display: flex;
  flex-direction: column;
  min-height: calc(100dvh - var(--v-layout-top));
  padding: 0px;
  @media (min-width: 496px) {
    padding: 12px;
  }
}

.col > .v-card,
[class*='col-'] > .v-card {
  height: calc(100% - 24px);
  margin: 12px;
}

.v-card {
  margin: 12px;
}

.v-card-title {
  padding: 16px;
  font-weight: 300;
  .header-icon {
    color: rgba(0, 0, 0, 0.76);
    margin-right: 8px;
  }
}

@media (min-width: 600px) {
  .v-list.inline {
    display: flex;
    flex-wrap: wrap;
    > .v-list-item {
      flex-basis: 50%;
    }
  }
}

.v-list-item-title,
.v-list-item-subtitle {
  font-size: 0.8125rem;
  font-weight: 500;
}

.registered-devices-container {
  position: relative;
  flex: 1;
  min-height: 0;
}

.registered-devices-card {
  tr.registered-device-mobile-table {
    border-bottom: thin solid rgba(0, 0, 0, 0.12) !important;
    td {
      border-bottom: unset !important;
    }
  }

  td.mobile-list {
    ul {
      padding-bottom: 16px;
      padding-left: 12px;
      list-style-type: none;
    }
    .item-title {
      display: flex;
      line-height: 36px;
      padding-top: 8px;
      .title-text {
        margin-left: 8px;
        font-weight: 700;
      }
    }
    .list-item {
      margin-bottom: 4px;
    }
    .mobile-header {
      display: block;
      margin-right: 4px;
      white-space: nowrap;
      color: rgba(0, 0, 0, 0.6);
    }
    .mobile-field {
      display: block;
      &.device-name {
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }
  }

  .device-name {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .v-btn {
    min-width: 120px !important;
    @media (min-width: 496px) {
      min-width: 160px !important;
    }
  }

  .v-btn.icon-button {
    min-width: 120px !important;
    width: 120px !important;
  }
}

.register-btn {
  margin-left: auto;
}

.emptyMessage {
  height: 240px;
  line-height: 240px;
}

.header-title {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 300;
}
</style>
